import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { getPubSubClient, eventToAvi } from '@confluence/pubsub-client';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver';
import { getSortedMarkerRefs } from '@confluence/comments-util';

type PubSubProps = BasePubsubProps & {
	getEditorAnnotations: () => string[];
	triggerAnalyticsEvent?:
		| ((
				eventName: string,
				extraData?: {
					[key: string]: any;
				},
		  ) => Promise<void>)
		| CreateUIAnalyticsEvent;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
};

type BasePubsubProps = {
	contentId: string;
	contentType?: string | null;
};

type CommentPublicationEventParams = {
	contentId?: string;
	getEditorAnnotations?: () => string[];
	triggerAnalyticsEvent?:
		| ((
				eventName: string,
				extraData?: {
					[key: string]: any;
				},
		  ) => Promise<void>)
		| CreateUIAnalyticsEvent;
	updateUnresolvedInlineComment?: (unresolvedInlineComments: string[]) => void;
};

let commentPublicationEventParams: CommentPublicationEventParams = {};
const setPublicationEventParams = (params: CommentPublicationEventParams) => {
	commentPublicationEventParams = params;
};

const onPublicationEvent = async (event: string, payload: any) => {
	const emitter = getEditorAnnotationEventEmitter();
	const { inlineCommentRequestBody } = payload;

	const { contentId, triggerAnalyticsEvent, getEditorAnnotations, updateUnresolvedInlineComment } =
		commentPublicationEventParams;

	switch (event) {
		case 'avi:confluence:unresolved:inline_comment':
			emitter.emit('unresolve', inlineCommentRequestBody.markerRef);
			break;
		case 'avi:confluence:resolved:inline_comment':
			emitter.emit('resolve', inlineCommentRequestBody.markerRef);
			break;
		case 'avi:confluence:created:inline_comment':
		case 'avi:confluence:created:editor_inline_comment':
			emitter.emit('create', inlineCommentRequestBody.markerRef);
			break;
		default:
			break;
	}

	// Updates ordered list of unresolved inline comments based on ADF to account for real-time changes (collaborative editing)
	if (
		event === 'avi:confluence:unresolved:inline_comment' ||
		event === 'avi:confluence:resolved:inline_comment' ||
		event === 'avi:confluence:created:inline_comment' ||
		event === 'avi:confluence:created:editor_inline_comment'
	) {
		if (
			getEditorAnnotations &&
			contentId &&
			triggerAnalyticsEvent &&
			updateUnresolvedInlineComment
		) {
			const annotations = getEditorAnnotations();
			void getEditorResolvedState({
				// the annotations won't already include the pubsub marker ref
				annotations: getSortedMarkerRefs(
					[...annotations, inlineCommentRequestBody.markerRef],
					'edit',
				),
				pageId: contentId,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
			});
		}
	}
};

export function setUpEditorInlineCommentsPubSub({
	contentId,
	contentType,
	getEditorAnnotations,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
}: PubSubProps) {
	const pubSubResourceType = contentType || 'page';

	setPublicationEventParams({
		contentId,
		getEditorAnnotations,
		triggerAnalyticsEvent,
		updateUnresolvedInlineComment,
	});

	void getPubSubClient().then((client) => {
		client
			.joinChannel(pubSubResourceType, contentId)
			.finally(() => {
				void client.subscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

				void client.subscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

				void client.subscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

				void client.subscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);
			})
			.catch((error) => {
				throw new Error(`PubSub Web Client joinChannel ${JSON.stringify(error)}`);
			});
	});
}

export function removeEditorInlineCommentsPubSub({ contentId, contentType }: BasePubsubProps) {
	const pubSubResourceType = contentType || 'page';

	void getPubSubClient().then((client) => {
		void client.leaveChannel(pubSubResourceType, contentId);
		void client.unsubscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

		void client.unsubscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

		void client.unsubscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

		void client.unsubscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);
	});
}
